import { toQueryString } from '@utils/requests';

import { request } from 'api/request';
import {
  ActionRelationship,
  BASE_ACTION_TYPE,
  CreateJourneyActionData,
  JourneyAction,
  JourneyActionDetailed,
  JourneyType,
} from 'api/types';
import { isInTemplateRoute } from 'ui/router';

export const loadJourneyActions = async (shortId: string) => {
  const response = await request.get<JourneyAction[]>(`v1/journeys/${shortId}/actions/`);
  return response.data;
};

export const loadJourneyTemplateActions = async (shortId: string) => {
  const response = await request.get<JourneyAction[]>(`v1/templates/journeys/${shortId}/actions/`);
  return response.data;
};

export const loadJourneyAction = async <ActionType extends BASE_ACTION_TYPE>(shortId: string) => {
  const publishedJourneyId = window.location.pathname.split('/')[2];
  const url = isInTemplateRoute()
    ? `v1/templates/journeys/${publishedJourneyId}/journey-actions`
    : 'v1/journey-actions';
  const response = await request.get<JourneyActionDetailed<ActionType>>(`${url}/${shortId}/`);
  return response.data;
};

export const loadJourneyActionIsInsideLoop = async (shortId: string) => {
  const response = await request.get<boolean>(`v1/journey-actions/${shortId}/is-inside-loop`);
  return response.data;
};

export const createJourneyAction = async (data: CreateJourneyActionData) => {
  const response = await request.post('v1/journey-actions/', data);
  return response.data;
};

export const updateJourneyAction = async (shortId: string, data: object) => {
  const response = await request.patch(`v1/journey-actions/${shortId}/`, data);
  return response.data;
};

export const updateJourneyActionDescription = async (shortId: string, description: string) => {
  const response = await request.patch(`v1/journey-actions/${shortId}/description/`, {
    description,
  });
  return response.data;
};

export const removeJourneyAction = async (shortId: string) => {
  const response = await request.delete(`v1/journey-actions/${shortId}/`);
  return response.data;
};

export const listJourneyActionRelationships = async (shortId: string) => {
  const url = isInTemplateRoute()
    ? `v1/templates/journey-actions/relationships/?journey_action=${shortId}`
    : `v1/journey-actions/relationships/?journey_action=${shortId}`;
  const response = await request.get<ActionRelationship>(url);
  return response.data;
};

export const createJourneyActionRelationship = async (data: { parent: string; child: string }) => {
  const response = await request.post('v1/journey-actions/relationships/', data);
  return response.data;
};

export const removeJourneyActionRelationship = async (shortId: string) => {
  const response = await request.delete(`v1/journey-actions/relationships/${shortId}/`);
  return response.data;
};

export type JourneyActionMoves = {
  [actionId in JourneyAction['shortId']]: {
    [otherId in JourneyAction['shortId']]: {
      [otherChildsId in JourneyAction['shortId']]: [string, string][];
    };
  };
};

export const loadJourneyActionAvailableMoves = async (journeyId: JourneyType['shortId']) => {
  const response = await request.get<JourneyActionMoves>(
    `v1/journey-actions/available-moves/?journey_id=${journeyId}`
  );
  return response.data;
};

type MoveJourneyActionData = {
  journeyId: JourneyType['shortId'];
  actionId: JourneyAction['shortId'];
  parentId: JourneyAction['shortId'];
  childId?: JourneyAction['shortId'];
};

export const moveJourneyAction = async (data: MoveJourneyActionData) => {
  const response = await request.post('v1/journey-actions/move/', data);
  return response.data;
};

type JourneyActionBreakingReferences = [string, string][];

export const listBreakingReferencesByMove = async ({
  actionId,
  parentId,
  childId,
}: {
  actionId: JourneyAction['shortId'];
  parentId: JourneyAction['shortId'];
  childId?: JourneyAction['shortId'];
}) => {
  const querystring = toQueryString({
    parent_id: parentId,
    child_id: childId,
  });
  const response = await request.get<JourneyActionBreakingReferences>(
    `v1/journey-actions/${actionId}/move/${querystring}`
  );
  return response.data;
};

export const listJourneyActionDependants = async (actionId: JourneyAction['shortId']) => {
  const response = await request.get<JourneyActionBreakingReferences>(
    `v1/journey-actions/${actionId}/dependants`
  );
  return response.data;
};

export const duplicateJourneyAction = async (shortId: string) => {
  const response = await request.post(`v1/journey-actions/${shortId}/duplicate/`);
  return response.data;
};

export const listBreakingReferencesByDeleteBranch = async (branchId: JourneyAction['shortId']) => {
  const response = await request.get<JourneyActionBreakingReferences>(
    `v1/journey-actions/branch-deletion-refs/${branchId}/`
  );
  return response.data;
};
